import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Bell } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Notification bell that polls the backend every 30s.
 * - For `audience='customer'`: requires customer token in headers.
 * - For `audience='staff'`: uses default axios Authorization (staff JWT).
 */
export const NotificationBell = ({ audience = 'staff', headers = {}, linkPrefix = '/dashboard/shop-orders' }) => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  const fetchNotifs = async () => {
    try {
      const url = audience === 'customer' ? `${API}/shop/notifications/customer` : `${API}/shop/notifications/staff`;
      const r = await axios.get(url, { headers });
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchNotifs();
    const t = setInterval(fetchNotifs, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const markRead = async (notif) => {
    if (notif.read) return;
    try {
      const url = audience === 'customer'
        ? `${API}/shop/notifications/customer/${notif.id}/read`
        : `${API}/shop/notifications/staff/${notif.id}/read`;
      await axios.post(url, {}, { headers });
      setItems(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      setUnread(u => Math.max(0, u - 1));
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={popRef}>
      <button onClick={() => setOpen(o => !o)} className="relative p-2 rounded-full hover:bg-muted/60 transition" data-testid={`notif-bell-${audience}`}>
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-semibold px-1" data-testid={`notif-unread-${audience}`}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-xl border border-border shadow-xl z-50">
          <div className="p-3 border-b border-border font-semibold text-sm">Notifications</div>
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No notifications yet.</div>
          ) : (
            <ul>
              {items.map(n => {
                const href = n.ref_id ? (audience === 'customer' ? `/shop/orders/${n.ref_id}` : `${linkPrefix}`) : null;
                const Body = (
                  <div className={`p-3 border-b border-border hover:bg-muted/30 ${n.read ? '' : 'bg-primary/5'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{n.title}</div>
                        <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</div>
                      </div>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-primary mt-1.5"></span>}
                    </div>
                  </div>
                );
                return (
                  <li key={n.id} onClick={() => markRead(n)} data-testid={`notif-item-${n.id}`}>
                    {href ? <Link to={href} onClick={() => setOpen(false)}>{Body}</Link> : Body}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
